// Camadas — a tela onde se cura.
//
// Grade de cards por tema, com os números que decidem o que abrir: quantas
// feições esperam validação, quantas já foram validadas, e se a fonte tem
// licença. Uma camada com 40 mil pendentes e fonte sem licença não é a mesma
// coisa que uma com 3 pendentes e licença registrada, e o card tem de deixar
// isso visível sem clicar.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import {
  alterar,
  enviar,
  obter,
  subirArquivo,
  type Camada,
  type Conector,
  type Conta,
  type Fonte,
  type Tema,
} from "../api.js";
import { Carregando, Falha, Pilula, quando, Vazio } from "../pecas/basicos.js";
import { GavetaCamada } from "./GavetaCamada.js";
import { EditarCamada } from "./EditarCamada.js";
import { NovaCamada } from "./NovaCamada.js";
import { Busca, casa } from "../pecas/Busca.js";
import { Confirmar } from "../pecas/Confirmar.js";

export function Camadas({
  avisar,
  abrir,
  aoFechar,
}: {
  avisar: (t: string, erro?: boolean) => void;
  // A Visão geral abre a gaveta de uma camada específica — é o atalho
  // principal daquela tela, e por isso a abertura pode vir de fora.
  abrir?: string | null;
  aoFechar?: () => void;
}): ReactNode {
  const [tema, setTema] = useState<string | null>(null);
  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<Camada | null>(null);
  const [arquivando, setArquivando] = useState<Camada | null>(null);
  // Arquivada some da operação por padrão — é o ponto de arquivar.
  const [verArquivadas, setVerArquivadas] = useState(false);
  const [busca, setBusca] = useState("");
  const [propria, setPropria] = useState<string | null>(null);
  const aberta = propria ?? abrir ?? null;
  const fechar = (): void => {
    setPropria(null);
    aoFechar?.();
  };
  const qc = useQueryClient();

  const temas = useQuery({ queryKey: ["temas"], queryFn: () => obter<Tema[]>("/temas") });
  const camadas = useQuery({
    queryKey: ["camadas", tema, verArquivadas],
    queryFn: () => {
      const q = new URLSearchParams();
      if (tema) q.set("tema", tema);
      if (verArquivadas) q.set("arquivadas", "1");
      return obter<Camada[]>(`/camadas${q.toString() ? `?${q.toString()}` : ""}`);
    },
  });

  // Só carregadas quando a gaveta de criação abre: são listas de cadastro, e
  // buscá-las em toda visita à tela de curadoria é requisição por nada.
  const fontes = useQuery({
    queryKey: ["fontes"],
    queryFn: () => obter<Fonte[]>("/fontes"),
    enabled: criando,
  });
  const contas = useQuery({
    queryKey: ["contas"],
    queryFn: () => obter<Conta[]>("/contas"),
    enabled: criando,
  });
  const conectores = useQuery({
    queryKey: ["conectores"],
    queryFn: () => obter<Conector[]>("/conectores"),
    enabled: criando,
  });

  const criar = useMutation({
    mutationFn: async (args: {
      camada: Record<string, unknown>;
      temaNovo: Record<string, string> | null;
    }) => {
      // O tema primeiro: a camada tem FK para ele, e criar a camada antes daria
      // erro de chave estrangeira em vez de "o tema ainda não existe".
      if (args.temaNovo) await enviar<Tema>("/temas", args.temaNovo);
      return enviar<Camada>("/camadas", args.camada);
    },
    onSuccess: (c) => {
      avisar(`Camada "${c.nome}" criada. Ingira ou suba um arquivo para começar a curadoria.`);
      setCriando(false);
      void qc.invalidateQueries({ queryKey: ["camadas"] });
      void qc.invalidateQueries({ queryKey: ["temas"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  const editar = useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
      alterar<Camada>(`/camadas/${id}`, corpo),
    onSuccess: () => {
      avisar("Camada salva.");
      setEditando(null);
      void qc.invalidateQueries({ queryKey: ["camadas"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  const arquivar = useMutation({
    mutationFn: ({ id, arquivar: arq }: { id: string; arquivar: boolean }) =>
      enviar<{ nome: string; arquivada: boolean }>(`/camadas/${id}/arquivar`, { arquivar: arq }),
    onSuccess: (r) => {
      avisar(
        r.arquivada
          ? `"${r.nome}" arquivada. As versões seladas continuam onde estavam.`
          : `"${r.nome}" de volta à operação.`,
      );
      setArquivando(null);
      void qc.invalidateQueries({ queryKey: ["camadas"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  const executar = useMutation({
    mutationFn: (id: string) => enviar<unknown>(`/camadas/${id}/executar`, { por: "curador" }),
    onSuccess: () => {
      avisar("Ingestão concluída.");
      void qc.invalidateQueries({ queryKey: ["camadas"] });
    },
    onError: (e: Error) => avisar(e.message, true),
  });

  if (camadas.isLoading || temas.isLoading) return <Carregando />;
  if (camadas.error) return <Falha erro={camadas.error} />;

  const todas = camadas.data ?? [];
  // Busca por nome E por órgão: quem procura "INCRA" quer as camadas daquele
  // órgão, não uma camada chamada INCRA.
  const lista = todas.filter((c) =>
    casa(`${c.nome} ${c.fonte_orgao} ${c.conta_nome ?? ""} ${c.projeto_nome ?? ""}`, busca),
  );
  return (
    <>
      <div className="barra-tela">
        <div className="filtros">
          <button className="chip" aria-pressed={tema === null} onClick={() => setTema(null)}>
            Todos os temas
          </button>
          {(temas.data ?? [])
            .filter((t) => t.camadas > 0)
            .map((t) => (
              <button
                key={t.id}
                className="chip"
                aria-pressed={tema === t.id}
                onClick={() => setTema(t.id)}
              >
                {t.nome} ({t.camadas})
              </button>
            ))}
        </div>
        <div className="linha-botoes">
          <Busca
            valor={busca}
            aoMudar={setBusca}
            rotulo="Buscar camada ou órgão"
            achados={lista.length}
            total={todas.length}
          />
          <button
            className="chip"
            aria-pressed={verArquivadas}
            onClick={() => setVerArquivadas(!verArquivadas)}
          >
            {verArquivadas ? "ocultar arquivadas" : "ver arquivadas"}
          </button>
          <button className="btn btn-primario" onClick={() => setCriando(true)}>
            Nova camada…
          </button>
        </div>
      </div>

      {lista.length === 0 ? (
        <Vazio>
          {tema === null
            ? busca.trim() !== ""
              ? `Nada encontrado para "${busca}".`
              : "Nenhuma camada cadastrada. Uma camada precisa de uma fonte — cadastre a fonte primeiro, se ainda não houver."
            : "Nenhuma camada neste tema."}
        </Vazio>
      ) : (
        <div className="grade-cards">
          {lista.map((c) => (
            <CardCamada
              key={c.id}
              camada={c}
              aoAbrir={() => setPropria(c.id)}
              aoEditar={() => setEditando(c)}
              aoArquivar={() => setArquivando(c)}
            />
          ))}
        </div>
      )}

      {editando ? (
        <EditarCamada
          camada={editando}
          aoFechar={() => setEditando(null)}
          aoSalvar={(corpo) => editar.mutate({ id: editando.id, corpo })}
          salvando={editar.isPending}
        />
      ) : null}

      {arquivando ? (
        <Confirmar
          titulo={arquivando.arquivada_em ? "Voltar à operação" : "Arquivar camada"}
          rotuloAcao={arquivando.arquivada_em ? "Desarquivar" : "Arquivar"}
          perigo={!arquivando.arquivada_em}
          descricao={
            arquivando.arquivada_em ? (
              <p>
                <b>{arquivando.nome}</b> volta a aparecer na operação. O agendamento continua
                desligado até você reativá-lo.
              </p>
            ) : (
              <>
                <p>
                  <b>{arquivando.nome}</b> sai da operação e o agendamento é desligado.
                </p>
                <p>
                  <b>Nada é apagado.</b> As versões seladas continuam onde estão — é o que permite a
                  um laudo citar o recorte da base que o sustentou, anos depois.
                </p>
              </>
            )
          }
          aoConfirmar={() =>
            arquivar.mutate({ id: arquivando.id, arquivar: !arquivando.arquivada_em })
          }
          aoCancelar={() => setArquivando(null)}
          pendente={arquivar.isPending}
        />
      ) : null}

      {criando ? (
        <NovaCamada
          temas={temas.data ?? []}
          fontes={fontes.data ?? []}
          conectores={conectores.data ?? []}
          contas={contas.data ?? []}
          aoFechar={() => setCriando(false)}
          aoSalvar={(camada, temaNovo) => criar.mutate({ camada, temaNovo })}
          salvando={criar.isPending}
        />
      ) : null}

      {aberta ? (
        <GavetaCamada
          camadaId={aberta}
          aoFechar={fechar}
          avisar={avisar}
          aoExecutar={(id) => executar.mutateAsync(id).then(() => undefined)}
          aoSubir={async (id, arquivo, categoria) => {
            const r = await subirArquivo(id, arquivo, categoria);
            // `inalteradas` sai junto de propósito: subir o mesmo arquivo de
            // novo passa a reportar 0 novas e 0 alteradas, e sem este número o
            // curador leria isso como "não importou nada".
            avisar(
              `${r.inseridas} nova(s), ${r.atualizadas} alterada(s)` +
                (r.inalteradas > 0 ? `, ${r.inalteradas} sem mudança` : "") +
                ` de "${r.arquivo}" (EPSG:${r.epsg_origem}); ${r.removidas} substituídas.`,
            );
            void qc.invalidateQueries({ queryKey: ["camadas"] });
          }}
          aoAlterar={async (id, corpo) => {
            await alterar(`/camadas/${id}`, corpo);
            void qc.invalidateQueries({ queryKey: ["camadas"] });
          }}
        />
      ) : null}
    </>
  );
}

function CardCamada({
  camada,
  aoAbrir,
  aoEditar,
  aoArquivar,
}: {
  camada: Camada;
  aoAbrir: () => void;
  aoEditar: () => void;
  aoArquivar: () => void;
}): ReactNode {
  const ultima = camada.ultima_execucao;
  // O card era um `<button>` inteiro, e botão dentro de botão é HTML inválido —
  // o navegador desfaz o aninhamento e as ações param de funcionar. Agora é uma
  // caixa com a área de abrir e um rodapé de ações.
  return (
    <div className={`card ${camada.arquivada_em ? "card-arquivado" : ""}`}>
      <button className="card-area" onClick={aoAbrir}>
        <div className="card-topo">
          <span className="card-nome">{camada.nome}</span>
          <Pilula status={camada.arquivada_em ? "arquivada" : camada.ativo ? "ativa" : "inativa"} />
        </div>
        <div className="card-fonte">
          {camada.fonte_orgao} · {camada.tipo_conector}
          {camada.conta_nome !== null ? (
            <>
              {" · "}
              <b>{camada.conta_nome}</b>
              {camada.projeto_nome !== null ? ` / ${camada.projeto_nome}` : ""}
            </>
          ) : null}
        </div>

        {/* Barra de funil: em que pé está a camada, de relance, sem ler número.
          As larguras são sobre (aguardando + validadas) — o universo daquela
          camada, não o do projeto. */}
        <div className="barra-funil">
          {larguras(camada).map(([cor, w]) => (
            <div key={cor} style={{ width: w, background: cor }} />
          ))}
        </div>

        <div className="card-numeros">
          <span>
            <b style={camada.aguardando > 0 ? { color: "var(--agd)" } : undefined}>
              {camada.aguardando}
            </b>
            <span>aguardando</span>
          </span>
          <span>
            <b>{camada.validadas}</b>
            <span>validadas</span>
          </span>
          <span>
            <b>{camada.ultima_versao ?? "—"}</b>
            <span>versão</span>
          </span>
        </div>

        {/* Licença ausente é o que impede publicar (princípio 5). Aparece no card
          porque descobrir isso só na hora de promover desperdiça a curadoria
          inteira. */}
        {!camada.fonte_publicavel ? (
          <div className="card-trava">Fonte sem licença ou atribuição — não publica.</div>
        ) : null}

        {ultima ? (
          <p className="card-rodape">
            Última ingestão {quando(ultima.executado_em)} · <Pilula status={ultima.status} />
          </p>
        ) : (
          <p className="card-rodape">Nunca executou.</p>
        )}
      </button>

      <div className="card-acoes">
        <button className="btn btn-pequeno" onClick={aoEditar}>
          Editar
        </button>
        <button className="btn btn-pequeno" onClick={aoArquivar}>
          {camada.arquivada_em ? "Desarquivar" : "Arquivar"}
        </button>
      </div>
    </div>
  );
}

/**
 * Larguras dos três segmentos do funil do card.
 *
 * O denominador é `aguardando + validadas`, não o total do projeto: o card fala
 * daquela camada. Sem feição nenhuma, a barra fica vazia em vez de dividir por
 * zero.
 */
function larguras(c: Camada): [string, string][] {
  const total = c.aguardando + c.validadas;
  if (total === 0) return [];
  const pct = (n: number): string => `${(n / total) * 100}%`;
  // `ultima_versao` existindo significa que parte do validado já virou versão.
  const publicadas = c.ultima_versao ? c.validadas : 0;
  return [
    ["var(--pub)", pct(publicadas)],
    ["var(--val)", pct(c.validadas - publicadas)],
    ["var(--agd)", pct(c.aguardando)],
  ];
}
